import { ArrowDownToLine, ArrowUpFromLine, Database, Info, Save } from 'lucide-react'
import { formatPrice } from '../utils/modelsFormatters'
import { economyTierTooltip, getDiscountedPrice, getTierDiscountPercents, getTierMultipliers, standardTierTooltip } from '../utils/tierPricing'
import { ModelDetailSection } from './ModelDetailSection'
import type { ChatModelPricing, UnifiedModel } from '../types/unified-model.types'
import type { ProviderTier } from '../../../../../shared-pricing-config'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface ModelDetailPricingProps {
  model: UnifiedModel & { availableTiers?: Array<ProviderTier>; hasDiscount?: boolean; discountPercent?: number }
}

const gridColsFor = (visibleTierCount: number) => {
  switch (visibleTierCount) {
    case 3:
      return 'grid-cols-[1fr_auto_auto_auto]'
    case 2:
      return 'grid-cols-[1fr_auto_auto]'
    default:
      return 'grid-cols-[1fr_auto]'
  }
}

export function ModelDetailPricing({ model }: ModelDetailPricingProps) {
  const p = model.pricing as ChatModelPricing
  const hasDiscount = model.hasDiscount && (model.discountPercent ?? 0) > 0
  const isAnthropic = model.family === 'anthropic'
  const cache1hOriginal = p.cacheCreation1hPricePerMillion ?? p.inputPricePerMillion * 2

  const showEconomy = model.availableTiers?.includes('economy') ?? false
  const showStandard = model.availableTiers?.includes('standard') ?? false
  const showPremium = model.availableTiers?.includes('premium') ?? false
  const showTierHeader = showEconomy || showStandard || showPremium
  const tierMultipliers = getTierMultipliers(model.id)
  const tierDiscountPercents = getTierDiscountPercents(model.id)
  const gridCols = gridColsFor([showEconomy, showStandard, showPremium].filter(Boolean).length)

  const disc = (price: number) => getDiscountedPrice(price, hasDiscount, model.discountPercent)
  const economyOf = (price: number) => disc(price) * tierMultipliers.economy
  const standardOf = (price: number) => disc(price) * tierMultipliers.standard
  const premiumOf = (price: number) => disc(price) * tierMultipliers.premium

  return (
    <ModelDetailSection title="Pricing" note="$ / 1M tokens">
      <TooltipProvider>
        <div className="surface-bevel overflow-hidden rounded-[1.5rem]">
          {showTierHeader && (
            <div className={cn('grid gap-x-4 border-b border-[var(--color-border)] px-5 py-3.5', gridCols)}>
              <span className="eyebrow self-center">Price</span>
              {showEconomy && (
                <span className="flex items-center justify-end gap-1 text-xs font-semibold text-[var(--color-info-text)]">
                  Economy <span className="font-normal opacity-80">(-{tierDiscountPercents.economy}%)</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="shadow-none! text-[var(--color-info-text)]/70 hover:text-[var(--color-info-text)] transition-colors">
                        <Info className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[220px]">{economyTierTooltip(tierDiscountPercents.economy)}</TooltipContent>
                  </Tooltip>
                </span>
              )}
              {showStandard && (
                <span className="flex items-center justify-end gap-1 text-xs font-semibold text-[var(--color-success-text)]">
                  Standard <span className="font-normal opacity-80">(-{tierDiscountPercents.standard}%)</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="shadow-none! text-[var(--color-success-text)]/70 hover:text-[var(--color-success-text)] transition-colors">
                        <Info className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[220px]">{standardTierTooltip(tierDiscountPercents.standard)}</TooltipContent>
                  </Tooltip>
                </span>
              )}
              {showPremium && (
                <span className="text-xs font-semibold text-[var(--color-text-tertiary)] text-right">Premium</span>
              )}
            </div>
          )}
  
          <div className="px-5">
            <PriceRow icon={ArrowDownToLine} label="Input" gridCols={gridCols}
              price={disc(p.inputPricePerMillion)} original={p.inputPricePerMillion} hasDiscount={hasDiscount}
              showEconomy={showEconomy} showStandard={showStandard} showPremium={showPremium}
              economyPrice={economyOf(p.inputPricePerMillion)} standardPrice={standardOf(p.inputPricePerMillion)} premiumPrice={premiumOf(p.inputPricePerMillion)} />
            <PriceRow icon={ArrowUpFromLine} label="Output" gridCols={gridCols}
              price={disc(p.outputPricePerMillion)} original={p.outputPricePerMillion} hasDiscount={hasDiscount}
              showEconomy={showEconomy} showStandard={showStandard} showPremium={showPremium}
              economyPrice={economyOf(p.outputPricePerMillion)} standardPrice={standardOf(p.outputPricePerMillion)} premiumPrice={premiumOf(p.outputPricePerMillion)} />
            <PriceRow icon={Database} label="Cache Read" gridCols={gridCols}
              price={disc(p.cacheReadPricePerMillion)} original={p.cacheReadPricePerMillion} hasDiscount={hasDiscount}
              showEconomy={showEconomy} showStandard={showStandard} showPremium={showPremium}
              economyPrice={economyOf(p.cacheReadPricePerMillion)} standardPrice={standardOf(p.cacheReadPricePerMillion)} premiumPrice={premiumOf(p.cacheReadPricePerMillion)} />
            <PriceRow icon={Save} label={isAnthropic ? 'Write (5m)' : 'Cache Write'} gridCols={gridCols}
              price={disc(p.cacheCreationPricePerMillion)} original={p.cacheCreationPricePerMillion} hasDiscount={hasDiscount}
              showEconomy={showEconomy} showStandard={showStandard} showPremium={showPremium}
              economyPrice={economyOf(p.cacheCreationPricePerMillion)} standardPrice={standardOf(p.cacheCreationPricePerMillion)} premiumPrice={premiumOf(p.cacheCreationPricePerMillion)} />
            {isAnthropic && (
              <PriceRow icon={Save} label="Write (1h)" gridCols={gridCols}
                price={disc(cache1hOriginal)} original={cache1hOriginal} hasDiscount={hasDiscount}
                showEconomy={showEconomy} showStandard={showStandard} showPremium={showPremium}
                economyPrice={economyOf(cache1hOriginal)} standardPrice={standardOf(cache1hOriginal)} premiumPrice={premiumOf(cache1hOriginal)} last />
            )}
          </div>
        </div>
      </TooltipProvider>
    </ModelDetailSection>
  )
}

function PriceRow({
  icon: Icon, label, price, original, hasDiscount, gridCols, showEconomy, showStandard, showPremium, economyPrice, standardPrice, premiumPrice, last,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  price: number
  original: number
  hasDiscount: boolean | undefined
  gridCols: string
  showEconomy: boolean
  showStandard: boolean
  showPremium: boolean
  economyPrice: number
  standardPrice: number
  premiumPrice: number
  last?: boolean
}) {
  const priceWithOriginal = (value: number) => (
    <div className="flex flex-col items-end justify-center leading-tight">
      <span className="font-mono font-semibold tabular-nums text-[var(--color-text-primary)]">{formatPrice(value)}/M</span>
      {hasDiscount && (
        <span className="font-mono text-xs text-[var(--color-text-tertiary)] line-through tabular-nums">{formatPrice(original)}/M</span>
      )}
    </div>
  )

  return (
    <div className={cn('grid gap-x-4 py-3.5 text-sm', gridCols, !last && 'border-b border-[var(--color-border)]')}>
      <div className="flex items-center gap-2 min-w-0 text-[var(--color-text-secondary)]">
        <Icon className="w-4 h-4 text-[var(--color-text-tertiary)] shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      {showEconomy && (
        <span className="font-mono font-semibold tabular-nums text-[var(--color-info-text)] text-right self-center">{formatPrice(economyPrice)}/M</span>
      )}
      {showStandard && (
        <span className="font-mono font-semibold tabular-nums text-[var(--color-success-text)] text-right self-center">{formatPrice(standardPrice)}/M</span>
      )}
      {showPremium && priceWithOriginal(premiumPrice)}
      {!showEconomy && !showStandard && !showPremium && priceWithOriginal(price)}
    </div>
  )
}
